import { ComponentProps, useEffect, useMemo, useState } from "react";
import {
  PlanningApplicationFeature,
  PlanningClassification,
  StatusDerived,
} from "react-migration/domains/planning/types";
import { Tag, TagColor } from "react-migration/components/Tag";

import { LTIcon } from "react-migration/components/LTIcon";
import { LayerTypeRightClickMenuProps } from "../../types";
import { Nullable } from "src/js/types/Nullable";
import { PickingInfo } from "deck.gl";
import { PlanningApplicationClassification } from "react-migration/domains/planning/components/PlanningApplicationClassification";
import { PlanningApplicationClusteredViewportSearchMapLayer } from "./PlanningApplicationsMapLayer/PlanningApplicationClusteredViewportSearchMapLayer";
import { RightClick } from "react-migration/components/RightClick";
import { SelectionFeature } from "src/js/stores/map/store";
import { Tooltip } from "react-migration/components/Tooltip";
import { feature } from "@turf/turf";
import { fetchPlanningApplicationById } from "./usePlanningAppsById";
import format from "date-fns/format";
import { getDeckInstance } from "src/js/plugins/deck.gl/deckInstance";
import { getDeckLayerInstanceIds } from "react-migration/lib/map/getLayerInstances";
import { isDefined } from "react-migration/lib/util/isDefined";
import { planningApplicationsClickResolver } from "./planningClickResolver";

interface PlanningApplicationMenuItem {
  id: string;
  description: string;
  statusDerived: Nullable<StatusDerived>;
  classification: PlanningClassification;
  ref: string;
  selectionFeature: SelectionFeature;
  dateReceived: string;
}

export function PlanningApplicationRightClickMenu({
  rightClickLocation,
  setSelection,
  setHoveredFeature,
}: LayerTypeRightClickMenuProps) {
  const [planningApplications, setPlanningApplications] = useState<PlanningApplicationMenuItem[]>();

  useEffect(() => {
    function getPlanningPicksFromDeck() {
      const [x, y] = rightClickLocation.deckCoordinate;

      const deck = getDeckInstance();
      const layerIds = getDeckLayerInstanceIds([PlanningApplicationClusteredViewportSearchMapLayer]);

      return deck?.pickMultipleObjects({ x, y, layerIds });
    }

    async function enrichPlanningApplicationPicks(picks: PickingInfo<PlanningApplicationFeature>[]) {
      const response = (
        await Promise.all(
          picks.map(async (pick) => {
            if (!pick.object) return;

            return Promise.all([
              fetchPlanningApplicationById(pick.object.properties.id),
              planningApplicationsClickResolver(pick),
            ]);
          })
        )
      )
        .filter(isDefined)
        .map(([planningApplication, selectionFeature]) => {
          if (!selectionFeature) return;

          return {
            id: planningApplication.id,
            description: planningApplication.title,
            classification: planningApplication.classification,
            ref: planningApplication.ref,
            dateReceived: planningApplication.date_received,
            selectionFeature,
            statusDerived: planningApplication.status_derived,
          };
        })
        .filter(isDefined);

      setPlanningApplications(response);
    }

    const planningPicks = getPlanningPicksFromDeck();
    planningPicks && enrichPlanningApplicationPicks(planningPicks);
  }, [rightClickLocation.deckCoordinate]);

  const bucketedApplications = useMemo(() => {
    const sortedApplications = planningApplications?.sort(
      (a, b) => new Date(b.dateReceived).getTime() - new Date(a.dateReceived).getTime()
    );

    if (!sortedApplications?.length) return [null, null];

    return [sortedApplications.slice(0, 4), sortedApplications.slice(4)];
  }, [planningApplications]);

  if (!bucketedApplications[0]?.length) return null;

  return (
    <RightClick.Group title="Planning Applications">
      {bucketedApplications[0].map((planningApplication) => (
        <PlanningApplicationItem
          key={planningApplication.id}
          planningApplication={planningApplication}
          setHoveredFeature={setHoveredFeature}
          setSelection={setSelection}
        />
      ))}

      {!!bucketedApplications[1]?.length && (
        <RightClick.Sub>
          <RightClick.SubTrigger>{bucketedApplications[1].length} more</RightClick.SubTrigger>
          <RightClick.Portal>
            <RightClick.SubContent>
              {bucketedApplications[1].map((planningApplication) => (
                <PlanningApplicationItem
                  key={planningApplication.id}
                  planningApplication={planningApplication}
                  setHoveredFeature={setHoveredFeature}
                  setSelection={setSelection}
                />
              ))}
            </RightClick.SubContent>
          </RightClick.Portal>
        </RightClick.Sub>
      )}
    </RightClick.Group>
  );
}

interface PlanningApplicationItemProps {
  planningApplication: PlanningApplicationMenuItem;
  setSelection(selection: SelectionFeature): void;
  setHoveredFeature(selection?: SelectionFeature): void;
}

function PlanningApplicationItem({
  planningApplication,
  setHoveredFeature,
  setSelection,
}: PlanningApplicationItemProps) {
  const renderGeometry = planningApplication.selectionFeature.__renderGeometry?.geometry;

  return (
    <RightClick.Item
      onClick={() => setSelection(planningApplication.selectionFeature)}
      onMouseEnter={() =>
        setHoveredFeature({
          ...planningApplication.selectionFeature,
          feature: renderGeometry ? feature(renderGeometry) : planningApplication.selectionFeature.feature,
        })
      }
      onMouseLeave={() => setHoveredFeature()}
      tailComponent={
        <div className="flex items-center gap-2">
          <Tooltip.Wrapper>
            <Tooltip.Trigger>
              <div
                className="text-sm text-blue-grey-400"
                // title={format(new Date(planningApplication.dateReceived), "dd/MM/yyyy")}
              >
                {format(new Date(planningApplication.dateReceived), "MM/yy")}
              </div>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content align="start" side="bottom" size="xs">
                {format(new Date(planningApplication.dateReceived), "dd/MM/yyyy")}
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Wrapper>
          <StatusDerivedTag statusDerived={planningApplication.statusDerived} />
        </div>
      }
    >
      <Tooltip.Wrapper>
        <Tooltip.Trigger>
          <div className="flex gap-2">
            <div className="shrink-0">
              <PlanningApplicationClassification classification={planningApplication.classification} />
            </div>
            <div>{planningApplication.ref}</div>
          </div>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content align="center" side="left" size="sm">
            <div className="text-xs text-blue-grey-950 whitespace-pre-line">{`${planningApplication.classification}: ${planningApplication.description}`}</div>
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Wrapper>
    </RightClick.Item>
  );
}

interface StatusDerivedTagProps {
  statusDerived: Nullable<StatusDerived>;
}

function StatusDerivedTag({ statusDerived }: StatusDerivedTagProps) {
  const tagProps = useMemo<Pick<ComponentProps<typeof Tag>, "color" | "tag" | "title">>(() => {
    switch (statusDerived) {
      case StatusDerived.APPROVED:
        return {
          color: TagColor.GREEN,
          tag: <LTIcon icon="lt-check-line" />,
          title: "Status: Approved",
        };

      case StatusDerived.REJECTED:
        return {
          color: TagColor.RED,
          tag: <LTIcon icon="lt-close-line" />,
          title: "Status: Rejected",
        };

      case StatusDerived.PENDING:
        return {
          color: TagColor.ORANGE,
          tag: <LTIcon icon="lt-clock-line" />,
          title: "Status: Pending",
        };

      case StatusDerived.WITHDRAWN:
        return {
          color: TagColor.LIGHT_GRAY,
          tag: <LTIcon icon="lt-close-line" />,
          title: "Status: Withdrawn",
        };

      default:
        return {
          color: TagColor.LIGHT_GRAY,
          tag: <LTIcon icon="lt-question-line" />,
          title: "Status: Unknown",
        };
    }
  }, [statusDerived]);

  return (
    <Tooltip.Wrapper>
      <Tooltip.Trigger>
        <div>
          <Tag color={tagProps.color} tag={tagProps.tag} className="px-1" />
        </div>
      </Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content align="start" side="bottom" size="xs">
          {tagProps.title}
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Wrapper>
  );
}
